import { Link } from "react-router-dom";
import { ArrowRight, Store, Handshake, Container, Tags, MapPinned, Network } from "lucide-react";
import { Seo } from "../components/Seo";
import { FadeUp, Stagger, StaggerItem } from "../components/Reveal";
import { IMAGES } from "../data/content";

const SERVICES = [
    { icon: Store, title: "Retail Distribution", desc: "Getting product onto NZ retail shelves — and keeping it there with the right ranging, pricing and promotional discipline." },
    { icon: Handshake, title: "Buyer Relationships", desc: "Preparing and running buyer conversations with grocery, hardware and specialty retail — from pitch decks to terms." },
    { icon: Container, title: "Import & Logistics", desc: "Landed-cost clarity, freight and supply chain setup that protects margin before the product ever hits a shelf." },
    { icon: Tags, title: "Wholesale Pricing", desc: "Pricing architecture that holds up under distributor, retailer and promotional pressure — without killing volume." },
    { icon: MapPinned, title: "Market Entry", desc: "Structured entry into new NZ regions, channels and industries — or first steps into export markets like Australia." },
    { icon: Network, title: "Channel Management", desc: "Distributor agreements, tiered incentives and scorecards that keep every channel partner accountable." },
];

const STEPS = [
    { step: "01", title: "Assess", desc: "Where can this product realistically win — and what will each route to market cost you in margin?" },
    { step: "02", title: "Plan", desc: "Channel strategy, pricing architecture and a 90-day entry plan with owners and milestones." },
    { step: "03", title: "Execute", desc: "Buyer meetings, distributor negotiations and launch execution — we sit alongside you in the room." },
    { step: "04", title: "Manage", desc: "Ongoing channel scorecards, promo calendars and terms reviews so the channel keeps performing." },
];

export default function MarketEntry() {
    return (
        <div data-testid="market-entry-page">
            <Seo
                title="NZ Market Entry & Distribution | GrowVise"
                description="Get your product onto NZ retail shelves and keep it there. Retail distribution, buyer relationships, import logistics and wholesale pricing expertise."
                path="/market-entry-distribution"
            />

            {/* Header — navy with natural image */}
            <section className="bg-navy-950 pt-32 sm:pt-40 pb-16 sm:pb-20" data-testid="me-hero">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <FadeUp className="lg:col-span-7">
                        <p className="eyebrow">NZ Market Entry &amp; Distribution</p>
                        <h1 className="mt-6 font-serif font-semibold text-white tracking-tight leading-[1.15] text-4xl sm:text-5xl max-w-xl">
                            Get onto NZ shelves. Stay there profitably.
                        </h1>
                        <p className="mt-6 text-base sm:text-lg text-slate-300/90 leading-relaxed max-w-xl">
                            Sixteen-plus years of hands-on experience in NZ retail distribution, importation, wholesale pricing and market entry — as both distributor and brand owner.
                        </p>
                        <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
                            <Link to="/book-a-conversation" className="btn-gold" data-testid="me-book-cta">
                                Book a Growth Conversation
                            </Link>
                            <Link to="/business-health-check" className="inline-flex items-center gap-2 text-sm font-medium text-gold-400 hover:text-gold-300 transition-colors duration-200" data-testid="me-assessment-cta">
                                Try the free Health Check <ArrowRight size={15} />
                            </Link>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-5">
                        <div className="rounded-lg overflow-hidden">
                            <img
                                src={IMAGES.marketEntry}
                                alt="Logistics fulfilment hub supporting nationwide NZ distribution"
                                className="w-full aspect-[4/3] object-cover"
                                loading="eager"
                            />
                        </div>
                        <p className="mt-3 text-[11px] font-mono tracking-widest uppercase text-slate-500">Freight, warehousing &amp; retail replenishment</p>
                    </FadeUp>
                </div>
            </section>

            {/* Services — white editorial rows */}
            <section className="section-pad bg-white" data-testid="me-services">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <FadeUp className="max-w-xl mb-14">
                        <p className="eyebrow-light">What we do</p>
                        <h2 className="mt-5 font-serif font-semibold text-ink tracking-tight leading-snug text-3xl sm:text-4xl">
                            Distribution expertise, commercially applied.
                        </h2>
                    </FadeUp>
                    <Stagger className="grid sm:grid-cols-2 gap-x-12">
                        {SERVICES.map((s) => (
                            <StaggerItem key={s.title}>
                                <div className="py-7 border-t border-navy-900/10" data-testid={`me-service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                    <div className="flex items-center gap-3">
                                        <s.icon size={18} className="text-gold-700 shrink-0" />
                                        <h3 className="font-serif font-semibold text-ink text-xl">{s.title}</h3>
                                    </div>
                                    <p className="text-sm text-warmgrey leading-relaxed mt-3">{s.desc}</p>
                                </div>
                            </StaggerItem>
                        ))}
                    </Stagger>
                </div>
            </section>

            {/* Process — mist */}
            <section className="section-pad bg-mist border-t border-navy-900/10" data-testid="me-process">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <FadeUp className="max-w-xl">
                        <p className="eyebrow-light">How engagements run</p>
                        <h2 className="mt-5 font-serif font-semibold text-ink tracking-tight leading-snug text-3xl sm:text-4xl">
                            From opportunity to operating channel.
                        </h2>
                    </FadeUp>
                    <Stagger className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12">
                        {STEPS.map((s) => (
                            <StaggerItem key={s.step}>
                                <div data-testid={`me-step-${s.step}`}>
                                    <p className="font-serif font-semibold text-5xl text-navy-900/15">{s.step}</p>
                                    <h3 className="font-serif font-semibold text-ink text-xl mt-4">{s.title}</h3>
                                    <p className="text-sm text-warmgrey leading-relaxed mt-3">{s.desc}</p>
                                </div>
                            </StaggerItem>
                        ))}
                    </Stagger>
                </div>
            </section>

            {/* CTA — navy band */}
            <section className="bg-navy-900 border-t border-white/10">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-16">
                    <FadeUp className="flex-1">
                        <h2 className="font-serif font-semibold text-white tracking-tight leading-snug text-3xl sm:text-4xl">
                            Thinking about a new channel or market?
                        </h2>
                        <p className="mt-4 text-slate-300/85 leading-relaxed max-w-xl">
                            A short conversation will tell you whether the opportunity is real — and what it would take to execute it.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.1} className="shrink-0">
                        <Link to="/book-a-conversation" className="btn-gold" data-testid="me-cta-book">
                            Book a Growth Conversation
                        </Link>
                    </FadeUp>
                </div>
            </section>
        </div>
    );
}
